import { authStore } from '@/stores';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { userConnectionService } from '@/modules/_core/services';
import { useSnackbar } from 'notistack';
import { AUTH_PAGE_URL, queryKeys } from '../constants';
import { UserDataType } from '../types/user-types';
import { UserToFollowerDto } from '@/../../libs/shared/src';
import { useGetPublicUserByUserNameQuery } from './queries';

export const useFollowCommunityMutation = (
	communityId: string,
	username: string
): { isFollowCommunityLoading: boolean; handleFollowCommunity: () => Promise<void> } => {
	const currentUser = authStore.user;
	const { enqueueSnackbar } = useSnackbar();
	const queryClient = useQueryClient();
	const { data: hostData } = useGetPublicUserByUserNameQuery(username);
	const userQueryKey = queryKeys.user(username);
	const previousData = queryClient.getQueryData<UserDataType>(userQueryKey);
	const isLoggedIn = currentUser?.id && authStore.isAuth;

	const { isLoading: isFollowCommunityLoading, mutateAsync: handleFollowCommunity } = useMutation({
		mutationFn: async () => {
			if (!communityId) {
				return;
			}

			// If user is not logged in, redirect to login
			if (!isLoggedIn) {
				window.open(`${AUTH_PAGE_URL}?redirect=${window.location.href}`, '_self');

				return;
			}

			await userConnectionService.follow(communityId);

			// Optimistic update
			if (previousData && currentUser) {
				let updatedFollowers = previousData.followers || [];

				const newFollowerEntry: UserToFollowerDto = {
					id: `temp-${Date.now()}`,
					userId: hostData.id,
					followerId: currentUser.id,
					isMutual: false,
					user: hostData,
					follower: currentUser,
					createdAt: new Date(),
				};
				updatedFollowers = [...updatedFollowers, newFollowerEntry];

				queryClient.setQueryData<UserDataType>(userQueryKey, {
					...previousData,
					isCurrentUserSubscribed: true,
					followersCount: previousData.followersCount + 1,
					followers: updatedFollowers,
				});
			}
		},
		onSuccess: () => {
			if (isLoggedIn) {
				enqueueSnackbar(`You've joined ${hostData.fullName}`, {
					variant: 'success',
				});
			}
		},
		onError: async (error: Error) => {
			if (previousData) {
				queryClient.setQueryData<UserDataType>(userQueryKey, previousData);
			}
			console.error(`Failed to join ${hostData.fullName}. Please try again.`, error);
			enqueueSnackbar(`Failed to join ${hostData.fullName}. Please try again.`, { variant: 'error' });
			await queryClient.invalidateQueries(userQueryKey);
		},
	});

	return { isFollowCommunityLoading, handleFollowCommunity };
};
